/**
 * As regras da lista de automações, sem React e sem rede.
 *
 * ── O que a tela mostra é o que o serviço tem ─────────────────────────────
 *
 * O protótipo desenhou números por automação, pendências e quem alterou por
 * último. A lista do `/v1/automacoes` não traz nada disso, e a tela não inventa:
 * mostra o nome, a versão, de onde a automação saiu, quando ela dispara e a
 * situação. O resto entra quando o serviço tiver.
 *
 * ── Quem mexe em quê ──────────────────────────────────────────────────────
 *
 * As mesmas guardas do backend (`apps/api/src/automacoes/automacoes.ts`), para
 * a tela não oferecer o que ele recusa:
 * - a empresa (gestor e administrador) mexe nas internas, a Cliqfy nas que leem
 *   o banco do cliente (`usa_sql`);
 * - ligar e pausar é de `ATIVA_AUTOMACAO`, e a Cliqfy só liga as com SQL;
 * - arquivar é de `ARQUIVA_AUTOMACAO`, com a mesma divisão de quem liga.
 * Quem não pode não vê o comando (docs/design: ação sem papel some).
 */

import type {
  BlocosDoEditor,
  ItemDaListaDeAutomacoes,
  ModelosDeAutomacao,
  PainelDasAutomacoes,
  SituacaoDaAutomacao,
  VersaoNoHistorico,
} from '@cliqcrm/contracts';
import {
  ARQUIVA_AUTOMACAO,
  ATIVA_AUTOMACAO,
  MONTA_AUTOMACAO_COM_SQL,
  MONTA_AUTOMACAO_INTERNA,
} from '@/entities/acesso/papel';
import type { NomeDoIcone, TomSelo } from '@/shared/ui';

export type Automacao = ItemDaListaDeAutomacoes;

/** Como cada situação aparece. "Em teste" é a homologação do serviço. */
export const SITUACAO: Record<
  SituacaoDaAutomacao,
  { rotulo: string; tom: TomSelo; icone: NomeDoIcone | null; vivo: boolean }
> = {
  rascunho: { rotulo: 'Rascunho', tom: 'rascunho', icone: 'lapis', vivo: false },
  em_homologacao: { rotulo: 'Em teste', tom: 'atencao', icone: 'frasco', vivo: false },
  ativa: { rotulo: 'Ativa', tom: 'ok', icone: null, vivo: true },
  pausada: { rotulo: 'Pausada', tom: 'neutro', icone: 'pausa', vivo: false },
  arquivada: { rotulo: 'Arquivada', tom: 'neutro', icone: null, vivo: false },
};

/** "Todas" é das em uso; "arquivada" lê outra lista, que só vem quando pedida. */
export type Filtro = 'todas' | SituacaoDaAutomacao;

export const FILTROS: readonly { valor: Filtro; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'ativa', rotulo: 'Ativas' },
  { valor: 'em_homologacao', rotulo: 'Em teste' },
  { valor: 'pausada', rotulo: 'Pausadas' },
  { valor: 'rascunho', rotulo: 'Rascunhos' },
  { valor: 'arquivada', rotulo: 'Arquivadas' },
];

/**
 * Quantas há em cada filtro. "Todas" são as em uso: as arquivadas não rodam e
 * só aparecem quando pedidas, como no serviço.
 */
export function contagens(
  emUso: readonly Automacao[],
  arquivadas: readonly Automacao[] | undefined,
): Partial<Record<Filtro, number>> {
  const por = (s: SituacaoDaAutomacao): number => emUso.filter((a) => a.situacao === s).length;
  return {
    todas: emUso.length,
    ativa: por('ativa'),
    em_homologacao: por('em_homologacao'),
    pausada: por('pausada'),
    rascunho: por('rascunho'),
    ...(arquivadas ? { arquivada: arquivadas.length } : {}),
  };
}

/** Sem distinção de caixa nem de acento: quem procura "lembrete" acha "Lembrete". */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

export function filtrar(
  itens: readonly Automacao[],
  filtro: Filtro,
  busca: string,
  nomeDe: (a: Automacao) => string,
): Automacao[] {
  const termo = normalizar(busca);
  return itens.filter(
    (a) =>
      (filtro === 'todas' || filtro === 'arquivada' || a.situacao === filtro) &&
      (termo === '' || normalizar(nomeDe(a)).includes(termo)),
  );
}

/**
 * A chave do modelo, sem o sufixo do serviço: um modelo pode virar várias
 * automações (`…:daily`), e todas são daquele modelo.
 */
function chaveDoModelo(modelo: string): string {
  return modelo.split(':')[0] ?? modelo;
}

/** O nome que aparece: o que a pessoa deu, o do modelo, ou um aviso de que falta. */
export function nomeDaAutomacao(a: Automacao, modelos: ModelosDeAutomacao | undefined): string {
  const proprio = a.nome?.trim();
  if (proprio) return proprio;
  const doModelo = modelos?.itens.find((m) => m.chave === chaveDoModelo(a.modelo))?.nome;
  return doModelo ?? 'Automação sem nome';
}

/** De onde ela saiu: o editor ou um modelo pronto. */
export function origemDaAutomacao(a: Automacao, modelos: ModelosDeAutomacao | undefined): string {
  if (a.modelo === 'personalizada') return 'Montada no editor';
  const nome = modelos?.itens.find((m) => m.chave === chaveDoModelo(a.modelo))?.nome;
  return nome ? `Do modelo “${nome}”` : 'De um modelo pronto';
}

/*
 * O ícone pelo modelo. As chaves são as do catálogo do serviço; uma que mudar
 * ou chegar nova cai no raio das automações, sem quebrar nada.
 */
const ICONE_DO_MODELO: Record<string, NomeDoIcone> = {
  'channel-menu-with-inactivity-close': 'menu',
  'opportunity-owner-from-conversation-taker': 'pessoa',
  'redistribute-on-no-first-reply': 'passar',
  'appointment-reminder-and-confirmation': 'calendario',
  'scheduled-records-to-crm': 'relogio',
};

export function iconeDoModelo(modelo: string): NomeDoIcone {
  return ICONE_DO_MODELO[chaveDoModelo(modelo)] ?? 'automacao';
}

export function iconeDaAutomacao(a: Automacao): NomeDoIcone {
  return iconeDoModelo(a.modelo);
}

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'] as const;

function hora(h: number, m: number): string {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Quando ela dispara, numa linha: o evento pelo nome do catálogo, ou os horários. */
export function resumoDoGatilho(a: Automacao, blocos: BlocosDoEditor | undefined): string {
  const g = a.gatilho;
  if (g.tipo === 'interno') return 'Disparada por outra automação do mesmo modelo';
  if (g.tipo === 'evento') {
    const tituloDe = (evento: string): string | undefined =>
      blocos?.eventos.find((e) => e.evento === evento)?.titulo;
    const titulo = tituloDe(g.evento);
    if (!titulo) return 'Quando acontece algo no CRM';
    // Os outros começos (#1183): "Quando: Uma conversa começou, ou a conversa foi reaberta".
    const outros = (a.outros_comecos ?? []).map((o) => tituloDe(o.evento) ?? 'outro evento');
    return outros.length === 0
      ? `Quando: ${titulo}`
      : `Quando: ${[titulo, ...outros.map((t) => t.charAt(0).toLowerCase() + t.slice(1))].join(', ou ')}`;
  }
  const horarios = g.horarios.map((x) =>
    x.dia_da_semana === null
      ? `todo dia às ${hora(x.hora, x.minuto)}`
      : `${DIAS[x.dia_da_semana]} às ${hora(x.hora, x.minuto)}`,
  );
  const primeiros = horarios.slice(0, 2).join(', ');
  const resto = horarios.length - 2;
  return `Num horário: ${primeiros}${resto > 0 ? ` e mais ${resto}` : ''}`;
}

// ── Quem mexe ────────────────────────────────────────────────────────────

/** Editar, mandar para teste e devolver para rascunho: o lado que monta aquele tipo. */
function podeMexer(papel: string, a: Pick<Automacao, 'usa_sql'>): boolean {
  return a.usa_sql ? MONTA_AUTOMACAO_COM_SQL.has(papel) : MONTA_AUTOMACAO_INTERNA.has(papel);
}

/** A Cliqfy põe no ar só as com SQL; o administrador, qualquer uma. */
function ladoQueLiga(papel: string, a: Automacao): boolean {
  return papel !== 'operacao_cliqfy' || a.usa_sql;
}

function podeLigar(papel: string, a: Automacao): boolean {
  return ATIVA_AUTOMACAO.has(papel) && ladoQueLiga(papel, a);
}

function podeArquivar(papel: string, a: Automacao): boolean {
  return ARQUIVA_AUTOMACAO.has(papel) && ladoQueLiga(papel, a);
}

export type Comando =
  'enviar' | 'voltar' | 'ativar' | 'pausar' | 'arquivar' | 'desarquivar' | 'excluir';

/**
 * Os comandos que cabem na situação e no papel. As transições são as do
 * serviço: rascunho → teste ou arquivada; teste → ativa ou rascunho; ativa →
 * pausada; pausada → ativa ou arquivada; arquivada → rascunho. Excluir de vez
 * (ADR-0029) é de quem arquiva, em toda situação menos a ativa: no ar, pause
 * antes.
 */
export function comandosDe(a: Automacao, papel: string | null): Comando[] {
  if (!papel) return [];
  const sim = (pode: boolean, c: Comando): Comando[] => (pode ? [c] : []);
  switch (a.situacao) {
    case 'rascunho':
      return [
        ...sim(podeMexer(papel, a), 'enviar'),
        ...sim(podeArquivar(papel, a), 'arquivar'),
        ...sim(podeArquivar(papel, a), 'excluir'),
      ];
    case 'em_homologacao':
      return [
        ...sim(podeLigar(papel, a), 'ativar'),
        ...sim(podeMexer(papel, a), 'voltar'),
        ...sim(podeArquivar(papel, a), 'excluir'),
      ];
    case 'ativa':
      return sim(podeLigar(papel, a), 'pausar');
    case 'pausada':
      return [
        ...sim(podeLigar(papel, a), 'ativar'),
        ...sim(podeArquivar(papel, a), 'arquivar'),
        ...sim(podeArquivar(papel, a), 'excluir'),
      ];
    case 'arquivada':
      return [
        ...sim(podeArquivar(papel, a), 'desarquivar'),
        ...sim(podeArquivar(papel, a), 'excluir'),
      ];
  }
}

/** Quem edita o mapa: o lado que monta aquele tipo, e não na arquivada. */
export function podeEditarMapa(a: Automacao, papel: string | null): boolean {
  return papel !== null && a.situacao !== 'arquivada' && podeMexer(papel, a);
}

/** O interruptor só existe entre ligada e pausada, e só para quem liga. */
export function temInterruptor(a: Automacao, papel: string | null): boolean {
  return (
    papel !== null && (a.situacao === 'ativa' || a.situacao === 'pausada') && podeLigar(papel, a)
  );
}

/**
 * Restaurar é editar: o lado que monta aquele tipo, e também o da versão que
 * volta (a antiga pode ter SQL e a atual não). A arquivada só sai por
 * desarquivar.
 */
export function podeRestaurar(
  a: Automacao,
  papel: string | null,
  foto: Pick<Automacao, 'usa_sql'>,
): boolean {
  return (
    papel !== null && a.situacao !== 'arquivada' && podeMexer(papel, a) && podeMexer(papel, foto)
  );
}

export const ROTULO_DO_COMANDO: Record<Comando, string> = {
  enviar: 'Enviar para teste',
  voltar: 'Voltar para rascunho',
  ativar: 'Ativar',
  pausar: 'Pausar',
  arquivar: 'Arquivar',
  desarquivar: 'Desarquivar',
  excluir: 'Excluir',
};

/** O que aconteceu, dito para quem acabou de clicar. */
export function recadoDoComando(
  c: Comando,
  nome: string,
  situacaoAntes: SituacaoDaAutomacao,
): string {
  switch (c) {
    case 'enviar':
      return `${nome} está em teste. Ela ainda não roda: falta ativar.`;
    case 'voltar':
      return `${nome} voltou para rascunho.`;
    case 'ativar':
      return situacaoAntes === 'pausada'
        ? `${nome} ligada. Volta a rodar a partir de agora.`
        : `${nome} está no ar. Ela roda a partir de agora.`;
    case 'pausar':
      return `${nome} pausada. Nada novo começa; o que estava em andamento termina.`;
    case 'arquivar':
      return `${nome} arquivada. Ela sai da lista e não roda.`;
    case 'desarquivar':
      return `${nome} voltou como rascunho.`;
    case 'excluir':
      return `${nome} foi excluída de vez.`;
  }
}

// ── Os números ───────────────────────────────────────────────────────────

/**
 * Os números das últimas 24 h. "Deram certo" é das que terminaram: a ignorada
 * (a condição não bateu) não falhou nem deu certo, e fica fora da conta. Sem
 * nenhuma terminada, 100%: nada deu errado.
 */
export function numerosDoPainel(p: PainelDasAutomacoes): {
  total: number;
  concluidas: number;
  ignoradas: number;
  falhas: number;
  taxa: number;
} {
  const { total, concluidas, ignoradas, falhas } = p.execucoes;
  const terminadas = concluidas + falhas;
  return {
    total,
    concluidas,
    ignoradas,
    falhas,
    taxa: terminadas === 0 ? 100 : Math.round((concluidas / terminadas) * 100),
  };
}

// ── O histórico ──────────────────────────────────────────────────────────

/*
 * O `resumo` do histórico não aparece: ele vem no vocabulário interno do
 * serviço ("→ active"). A linha diz o que aconteceu pela ação, que o CRM já
 * traduz, e pela situação da foto.
 */
const ROTULO_DA_ACAO: Record<string, string> = {
  criada: 'Criada',
  editada: 'Alterada',
  restaurada: 'Restaurada a partir de uma versão anterior',
};

export function oQueAconteceu(v: VersaoNoHistorico): string {
  if (v.acao === 'situacao_mudou') {
    return v.foto ? `Passou para “${SITUACAO[v.foto.situacao].rotulo}”` : 'Mudou de situação';
  }
  return ROTULO_DA_ACAO[v.acao] ?? 'Alterada';
}

/**
 * Quem fez. O histórico traz só o id da pessoa, e para o gestor não há de onde
 * tirar o nome: a tela diz "Você" quando é ela, e não inventa os outros.
 */
export function quemFez(v: VersaoNoHistorico, meuId: string | null): string {
  return meuId !== null && v.autor_id === meuId ? 'Você' : 'Outra pessoa da equipe';
}

/**
 * Onde oferecer "Restaurar": uma vez por versão, na linha mais nova dela que
 * tem a foto, e nunca na versão de agora. O histórico tem várias linhas por
 * versão (criada, depois mudou de situação), e restaurar é pela versão.
 */
export function linhasQueRestauram(
  itens: readonly VersaoNoHistorico[],
  atual: number,
): Set<number> {
  const vistas = new Set<number>();
  const linhas = new Set<number>();
  itens.forEach((v, i) => {
    if (v.versao === atual || vistas.has(v.versao) || !v.foto) return;
    vistas.add(v.versao);
    linhas.add(i);
  });
  return linhas;
}

const QUANDO = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function quando(iso: string): string {
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? '' : QUANDO.format(data);
}
